import React, {useEffect, useState} from 'react';
import {capitalizeString, getName, remainingLeave} from "../../../../utility/utility";
import {IEmployeeLeaveDetail, ILeaveBalance, ILeaveType} from "./leave-summary.types";
import Grid from "../../../../common/table";
import LeavePeriodDropdown from "./component/leave-period-dropdown";
import server from "../../../../server";
import EmployeeAvatar from "../../../../page-components/employee/avatar/employee-avatar";
import EmployeeOverviewPopover
    from "../../../../page-components/employee/overview/employee-overview-popover/employee-overview-popover";
import RequestCountChart from "./charts/request-count-chart";
import {LeaveRequestSummary} from "../../../../domains/models/leave";
import '../../../../styles/component/leave-summary.scss'
import LeaveTypeAnaylsis from "./charts/leave-type-anaylsis-chart";
import StatsChart from "../../../../page-components/common/stats-chart/stats-chart";
import {PieChart as PieIcon} from "lucide-react";
import LoadingSkeleton from "../../../../page-components/common/loading-skeleton/loading-skeleton";

interface ILeaveSummaryProp {
    monthList: string[]
}

interface ILeaveSummaryState {
    employeeLeaveDetail: IEmployeeLeaveDetail[],
    leaveRequestsCount: LeaveRequestSummary | null;
    leaveTypeAnalysis: Map<string, number> | null;
    leavePeriodId: string;
}

const LeaveSummaryAdmin = (props: ILeaveSummaryProp) => {
    const {monthList} = props;
    const [leaveRequestLoading, setLeaveRequestLoading] = useState(true);
    const [leaveBalanceLoading, setLeaveBalanceLoading] = useState(true);

    const [summaryData, setSummaryData] = useState<ILeaveSummaryState>({
        employeeLeaveDetail: [],
        leaveRequestsCount: null,
        leaveTypeAnalysis: null,
        leavePeriodId: '',
    });

    const [pagination, setPagination] = useState({
        pageNumber: 0,
        pageSize: 10
    })

    useEffect(() => {
        if (!summaryData.leavePeriodId) return;
        leaveBalance();
        leaveSummaryCount();
    }, [summaryData.leavePeriodId]);

    async function leaveBalance(pageNumber?: number) {
        setLeaveBalanceLoading(true)
        try {
            const payload = {
                leavePeriodId: summaryData.leavePeriodId
            }
            const response = await server.leave.balance.search(payload, pageNumber ?? pagination.pageNumber, pagination.pageSize);
            processData(response);
        } catch (e) {
            console.log("Error while fetching leave balance")
        } finally {
            setLeaveBalanceLoading(false)
        }
    }

    async function leaveSummaryCount() {
        try {
            setLeaveRequestLoading(true);
            if (!summaryData.leavePeriodId) return;
            const response = await server.leave.leaveSummary.list(summaryData.leavePeriodId);
            setSummaryData(prev => ({
                ...prev,
                leaveRequestsCount: response,
            }));
        } catch (e) {
            console.log("Error while fetching leave summary")
        } finally {
            setLeaveRequestLoading(false);
        }
    }

    function processData(response: ILeaveBalance[]) {
        const employeeDetails: any[] = [];
        const leaveTotals = new Map<string, number>();

        response.map((item: ILeaveBalance) => {
            employeeDetails.push({
                id: item.employeeId,
                name: getName(item.employeeName.firstName, '', item.employeeName.lastName),
                employeeName: item.employeeName,
                email: item.email,
                allottedLeave: item.unit,
                profilePhotoThumbnailId: item.profilePhotoThumbnailId,
                availableBalance: item.unit - remainingLeave(item),
                totalApplied: item.pending + item.approved,
                leaveType: getLeaveTypeData(item.leaves),
                //get total month-wise applied leaves
                monthLeaveData: getMonthData(item.leaves)
            });
            // Pie chart totals
            item.leaves.forEach(l => {
                leaveTotals.set(l.type, (leaveTotals.get(l.type) || 0) + (l.applied ?? 0));
            });
        });

        // Set final data
        setSummaryData(prev => ({
            ...prev,
            employeeLeaveDetail: employeeDetails,
            leaveTypeAnalysis: leaveTotals
        }));
    }

    const handleLeavePeriodId = (val: string) => {
        if (val) {
            setSummaryData((prev) => ({
                ...prev,
                leavePeriodId: val
            }))
        }
    }

    function getLeaveTypeData(leaves: ILeaveType[]) {
        const leaveData = leaves.map((l) => {
            return {
                leaveType: l.type,
                allotted: l.unit,
                balance: l.balance,
                taken: l.unit - (l.pending + l.approved),
                pending: l.pending,
                approved: l.approved,
                rejected: l.rejected,
                cancelled: l.cancelled
            }
        });
        return leaveData ?? [];
    }

    function getMonthData(leaves: ILeaveType[]): { [key: string]: number } {
        const monthTotals: Record<string, number> = {};
        monthList.forEach(m => monthTotals[m] = 0);
        leaves.forEach(l => {
            l.months.forEach(m => {
                const key = capitalizeString(m.month).slice(0, 3); // "Jan", "Feb"
                if (monthTotals[key] !== undefined) {
                    monthTotals[key] += m.applied ?? 0;
                }
            });
        });
        return monthTotals;
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            //      width: 100,
            render: (_: any, record: any) => {
                const fullName = getName(record.employeeName.firstName, '', record.employeeName.lastName)
                return (
                    <div className="employee-name-wrapper">
                        <EmployeeAvatar
                            name={record.employeeName}
                            thumbnailId={record.profilePhotoThumbnailId}
                            size={28}
                        />
                        <EmployeeOverviewPopover employeeId={record.id}>
                            <div className="employee-search-table-name">
                                {fullName}
                            </div>
                        </EmployeeOverviewPopover>
                    </div>
                );
            }
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            // filterable: true,
            fixed: 'left',
            //   width: 100
        },
        {
            title: 'Units',
            dataIndex: 'allottedLeave',
            key: 'allottedLeave',
            fixed: 'left',
            //   width: 100
        },
        {
            title: 'Left',
            dataIndex: 'availableBalance',
            key: 'availableBalance',
            fixed: 'left',
            //  width: 100
        },
        ...monthList.map(item => ({
            title: item,
            dataIndex: 'monthlyLeaves',
            key: item.toLowerCase(),
            width: 15,
            render: (text: string, record: any) => {
                const value = record.monthLeaveData[item] ?? 0;
                return <span>{value}</span>;
            }
        })),
        {
            title: 'Total Applied',
            dataIndex: 'totalApplied',
            key: 'totalApplied',
            fixed: 'right',
            width: 70,
        }
    ];

    async function handleNextPage() {
        const nextPage = pagination.pageNumber + 1
        setPagination(prevState => ({
            ...prevState,
            pageNumber: nextPage
        }));
        leaveBalance(nextPage);
    }

    async function handlePrevPage() {
        const prevPage = pagination.pageNumber > 0 ? pagination.pageNumber - 1 : 0;
        setPagination(prevState => ({
            ...prevState,
            pageNumber: prevPage
        }));
        leaveBalance(prevPage);
    }

    return (
        <div className={'leave-summary-admin__layout'}>
            <LeavePeriodDropdown setLeavePeriod={handleLeavePeriodId}/>
            <section className={'leave-summary-admin__leave-section'}>
                <StatsChart
                    title={"Leave Request Analysis"}
                    icon={PieIcon}
                    badge={{
                        text: summaryData.leaveRequestsCount?.totalRequest ? `Total ${summaryData.leaveRequestsCount?.totalRequest} Requests` : "",
                        color: 'purple'
                    }}
                >
                    <RequestCountChart
                        summary={summaryData.leaveRequestsCount}
                        loading={leaveRequestLoading}/>
                </StatsChart>

                <StatsChart
                    title={"Leave type distribution"}
                    icon={PieIcon}
                >
                    <LeaveTypeAnaylsis
                        summary={summaryData.leaveTypeAnalysis}
                        leaveBalanceLoading={leaveBalanceLoading}/>
                </StatsChart>
            </section>

            <section className={'leave-summary-admin__leave-tracker'}>
                <h3 className={'title'}>Employee Leave tracker</h3>
                <LoadingSkeleton loading={leaveBalanceLoading}>
                    <Grid
                        bordered={true}
                        columns={columns}
                        scrollable={true}
                        dataSource={summaryData?.employeeLeaveDetail}
                        paginationButton={true}
                        handleNextPage={handleNextPage}
                        handlePrevPage={handlePrevPage}
                        pageNumber={pagination.pageNumber}
                        pageSize={pagination.pageSize}
                    />
                </LoadingSkeleton>
            </section>
        </div>
    );
}

export default LeaveSummaryAdmin;
